/**
 * Task 15 — Supabase transport adapter over @supabase/supabase-js.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CompoundCursor,
  OptimisticWriteOptions,
  RemoteTransport,
  TransportError,
  TransportResult,
} from './remoteTransport';

function cursorUpdatedAtForQuery(lastUpdatedAt: string): string {
  if (lastUpdatedAt === 'epoch') return '1970-01-01T00:00:00.000Z';
  return lastUpdatedAt;
}

function asTransportError(error: unknown): TransportError | null {
  if (error === null || error === undefined) return null;
  if (typeof error !== 'object') return { message: 'unknown_transport_error' };
  const e = error as { message?: unknown; code?: unknown; status?: unknown };
  const message = typeof e.message === 'string' ? e.message : 'unknown_transport_error';
  const out: TransportError = { message };
  if (typeof e.code === 'string') out.code = e.code;
  if (typeof e.status === 'number') out.status = e.status;
  return out;
}

function asRow(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined) return null;
  if (typeof data !== 'object' || Array.isArray(data)) return null;
  return data as Record<string, unknown>;
}

async function awaitPostgrest(query: PromiseLike<unknown>): Promise<{
  data: unknown;
  error: TransportError | null;
}> {
  const result: unknown = await query;
  if (typeof result !== 'object' || result === null) {
    return { data: null, error: { message: 'invalid_response' } };
  }
  const r = result as { data?: unknown; error?: unknown };
  return { data: r.data ?? null, error: asTransportError(r.error ?? null) };
}

export function createSupabaseTransport(client: SupabaseClient): RemoteTransport {
  return {
    async insert(table, row): Promise<TransportResult<Record<string, unknown> | null>> {
      const { data, error } = await awaitPostgrest(
        client.from(table).insert(row).select('*').maybeSingle() as PromiseLike<unknown>,
      );
      return { data: asRow(data), error };
    },

    async update(
      table,
      id,
      row,
      options?: OptimisticWriteOptions,
    ): Promise<TransportResult<Record<string, unknown> | null>> {
      let query = client.from(table).update(row).eq('id', id);
      if (options !== undefined) {
        query = query.eq('updated_at', options.expectedUpdatedAt);
      }
      const { data, error } = await awaitPostgrest(
        query.select('*').maybeSingle() as PromiseLike<unknown>,
      );
      return { data: asRow(data), error };
    },

    async softDelete(
      table,
      id,
      deletedAt,
      options?: OptimisticWriteOptions,
    ): Promise<TransportResult<Record<string, unknown> | null>> {
      let query = client.from(table).update({ deleted_at: deletedAt }).eq('id', id);
      if (options !== undefined) {
        query = query.eq('updated_at', options.expectedUpdatedAt);
      }
      const { data, error } = await awaitPostgrest(
        query.select('*').maybeSingle() as PromiseLike<unknown>,
      );
      return { data: asRow(data), error };
    },

    async selectById(table, id): Promise<TransportResult<Record<string, unknown> | null>> {
      const { data, error } = await awaitPostgrest(
        client.from(table).select('*').eq('id', id).maybeSingle() as PromiseLike<unknown>,
      );
      return { data: asRow(data), error };
    },

    async list(table): Promise<TransportResult<readonly Record<string, unknown>[]>> {
      const { data, error } = await awaitPostgrest(
        client.from(table).select('*') as PromiseLike<unknown>,
      );
      if (error) return { data: [], error };
      const rows = Array.isArray(data)
        ? data.filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
        : [];
      return { data: rows, error: null };
    },

    async selectSince(
      table,
      cursor: CompoundCursor,
      limit: number,
    ): Promise<TransportResult<readonly Record<string, unknown>[]>> {
      const ts = cursorUpdatedAtForQuery(cursor.lastUpdatedAt);
      // Quote timestamps — colons break unquoted PostgREST filter values.
      const tsLit = `"${ts}"`;
      const idLit = `"${cursor.lastId}"`;
      // PostgREST: updated_at > ts OR (updated_at = ts AND id > lastId)
      const filter = `updated_at.gt.${tsLit},and(updated_at.eq.${tsLit},id.gt.${idLit})`;
      const { data, error } = await awaitPostgrest(
        client
          .from(table)
          .select('*')
          .or(filter)
          .order('updated_at', { ascending: true })
          .order('id', { ascending: true })
          .limit(limit) as PromiseLike<unknown>,
      );
      if (error) return { data: [], error };
      const rows = Array.isArray(data)
        ? data.filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
        : [];
      return { data: rows, error: null };
    },
  };
}
